import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, ShieldAlert, KeyRound, Check } from 'lucide-react';
import api from '../lib/api';
import { Pill, money, when } from './PlatformConsole';
import { ConfirmAction } from './PlatformPharmacies';
import { PlatformDesktopRelease } from './PlatformDesktopRelease';
import { PlatformRollout } from './PlatformRollout';

// Desktop/security statuses are mapped onto the existing pill palette
// (active=green, past_due=amber, suspended=red, pending=grey) instead of
// inventing new colours.
const DESKTOP_PILL = { online: 'active', idle: 'past_due', quiet: 'past_due', signed_out: 'pending', no_desktop: 'pending' };
const SECURITY_PILL = { critical: 'suspended', attention: 'past_due', ok: 'approved' };

const StatusPill = ({ map, value, fallback = 'pending' }) => <Pill value={map[value] || fallback} />;

const AlertsStrip = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get('/platform/installations/alerts').then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const ack = async (alertId) => {
    try {
      await api.post(`/platform/installations/alerts/${alertId}/acknowledge`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not acknowledge'); }
  };
  if (!data || data.open_count === 0) return null;
  const open = data.alerts.filter(a => !a.acknowledged);
  return (
    <div className="pc-card" style={{ marginBottom: '1.1rem', borderColor: '#6e2a2a' }} data-testid="pc-install-alerts">
      <div className="pc-card-h"><h2><ShieldAlert className="pc-inline-icon" style={{ color: '#ff9b9b' }} />{open.length} branch alert(s) need attention</h2></div>
      <table className="pc-table">
        <tbody>
          {open.map(a => (
            <tr key={a.alert_id} data-testid={`pc-install-alert-${a.alert_id}`}>
              <td><Pill value={a.severity === 'critical' ? 'suspended' : 'past_due'} /></td>
              <td>
                <b style={{ color: '#fff' }}>{a.branch_name}</b>
                <div className="pc-sub" style={{ marginTop: '.15rem' }}>{a.message}</div>
                <div className="pc-muted" style={{ marginTop: '.2rem' }}>{a.cause} — {a.action}</div>
              </td>
              <td className="pc-num">
                <Link className="pc-btn" to={`/platform/installations/${a.tenant_id}`} data-testid={`pc-install-alert-open-${a.alert_id}`}>Open</Link>
              </td>
              <td className="pc-num">
                <button type="button" className="pc-btn" onClick={() => ack(a.alert_id)} data-testid={`pc-install-alert-ack-${a.alert_id}`}>
                  <Check className="h-3.5 w-3.5 inline mr-1" />Acknowledge
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PlatformInstallations = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/platform/installations').then(r => setData(r.data)).catch(() => toast.error('Could not load installations'));
  }, []);

  const rows = data?.installations || [];
  const counts = data?.counts || {};

  return (
    <div data-testid="pc-installations">
      <h1 className="pc-h1">Installations</h1>
      <p className="pc-sub">Every branch's desktop status, subscription and security health in one place.</p>

      <div className="pc-grid">
        {[['Total branches', counts.total], ['Desktop online', counts.online], ['No desktop installed', counts.no_desktop],
          ['Needs attention', counts.attention], ['Overdue', counts.overdue]].map(([label, value]) => (
          <div className="pc-tile" key={label} data-testid={`pc-install-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <b>{value ?? 0}</b><span>{label}</span>
          </div>
        ))}
      </div>

      <PlatformDesktopRelease />

      <PlatformRollout />

      <AlertsStrip />

      {!data && <div className="pc-muted" data-testid="pc-installations-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>}
      {data && (
        <div className="pc-card pc-scroll pc-installations-list">
          <table className="pc-table">
            <thead><tr>
              <th>Branch</th><th>Desktop</th><th>Last seen</th><th>Version</th><th>Sync</th>
              <th>Plan</th><th>Renews</th><th>Security</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.tenant_id} onClick={() => navigate(`/platform/installations/${r.tenant_id}`)}
                  data-testid={`pc-install-row-${r.tenant_id}`}>
                  <td><b style={{ color: '#fff' }}>{r.branch_name}</b><div className="pc-mono">{r.pharmacy_name}</div></td>
                  <td><StatusPill map={DESKTOP_PILL} value={r.desktop_status} /></td>
                  <td>{r.last_seen ? when(r.last_seen) : '—'}</td>
                  <td className="pc-mono">{r.app_version || '—'}</td>
                  <td><StatusPill map={SECURITY_PILL} value={r.sync_health} fallback="approved" /></td>
                  <td style={{ textTransform: 'capitalize' }}>{r.plan_tier}</td>
                  <td>{r.overdue ? <span style={{ color: '#ff9b9b' }}>{when(r.renews_at)} · overdue</span> : when(r.renews_at)}</td>
                  <td><StatusPill map={SECURITY_PILL} value={r.security_level} fallback="approved" /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="pc-empty" data-testid="pc-no-installations">No branches yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const PlatformInstallationDetail = () => {
  const { tenantId } = useParams();
  const [data, setData] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [params, setParams] = useSearchParams();
  const contactRequested = params.get('contact') === '1';
  const persistedContactTenantId = data?.tenant?.tenant_id;
  const persistedContactEmail = data?.tenant?.email || '';
  const persistedContactPhone = data?.tenant?.phone || '';

  const load = useCallback(() => {
    api.get(`/platform/installations/${tenantId}`).then(r => setData(r.data)).catch(() => toast.error('Could not load this branch'));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (persistedContactTenantId && contactRequested) {
      setContact({ email: persistedContactEmail, phone: persistedContactPhone });
      setContactOpen(true);
    }
  }, [persistedContactTenantId, persistedContactEmail, persistedContactPhone, contactRequested]);

  const openContact = () => {
    setContact({ email: data?.tenant?.email || '', phone: data?.tenant?.phone || '' });
    setContactOpen(true);
  };

  const doReset = async (reason) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(
        `/platform/installations/${tenantId}/users/${resetTarget.user_id}/reset-password`, { reason },
      );
      setTempPassword(res);
      setResetTarget(null);
      toast.success(`Password reset for ${res.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
    setBusy(false);
  };

  const saveContact = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/installations/${tenantId}/contact`, contact);
      toast.success('Reminder contact updated'); setContactOpen(false); setParams({}, { replace: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the contact'); }
    setBusy(false);
  };

  if (!data) return <div className="pc-muted" data-testid="pc-install-detail-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const { tenant, terminals, users, invoices, subscription, security_events: securityEvents } = data;

  return (
    <div data-testid="pc-install-detail">
      <Link to="/platform/installations" className="pc-btn" style={{ display: 'inline-flex', marginBottom: '1rem' }} data-testid="pc-install-back">
        <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> Installations
      </Link>
      <h1 className="pc-h1">{tenant.name}</h1>
      <p className="pc-sub">{tenant.tenant_id} · plan {subscription.tier || '—'} ({subscription.status || 'none'})</p>

      <div className="pc-split pc-install-detail-grid" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card">
          <div className="pc-card-h"><h2>Desktop terminals</h2></div>
          <table className="pc-table">
            <thead><tr><th>Terminal</th><th>User</th><th>Status</th><th>Version</th><th>Last seen</th></tr></thead>
            <tbody>
              {terminals.map(t => (
                <tr key={t.terminal_id} data-testid={`pc-install-terminal-${t.terminal_id}`}>
                  <td>{t.label || t.terminal_id}</td>
                  <td>{t.user_name || '—'}</td>
                  <td><StatusPill map={DESKTOP_PILL} value={t.status} /></td>
                  <td className="pc-mono">{t.app_version || '—'}</td>
                  <td>{when(t.last_seen)}</td>
                </tr>
              ))}
              {terminals.length === 0 && <tr><td colSpan={5} className="pc-empty" data-testid="pc-install-no-terminals">No desktop terminals registered — web-only.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="pc-card">
          <div className="pc-card-h"><h2>Subscription</h2></div>
          <table className="pc-table">
            <tbody>
              <tr><td>Plan</td><td className="pc-num" style={{ textTransform: 'capitalize' }}>{subscription.tier || '—'}</td></tr>
              <tr><td>Status</td><td className="pc-num"><Pill value={subscription.status} /></td></tr>
              <tr><td>Renews / renewed</td><td className="pc-num">{when(subscription.current_period_end)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="pc-card" data-testid="pc-install-contact-card">
          <div className="pc-card-h"><h2>Reminder contact</h2><button type="button" className="pc-btn" onClick={openContact} data-testid="pc-install-edit-contact">Edit</button></div>
          <table className="pc-table"><tbody><tr><td>Email</td><td className="pc-num" data-testid="pc-install-contact-email">{tenant.email || '—'}</td></tr><tr><td>Ghana phone</td><td className="pc-num" data-testid="pc-install-contact-phone">{tenant.phone || '—'}</td></tr></tbody></table>
        </div>

        <div className="pc-card">
          <div className="pc-card-h"><h2>Users at this branch</h2></div>
          <table className="pc-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th /></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} data-testid={`pc-install-user-${u.user_id}`}>
                  <td>{u.name}</td><td className="pc-mono">{u.email}</td>
                  <td style={{ textTransform: 'capitalize' }}>{u.role}</td>
                  <td className="pc-num">
                    <button type="button" className="pc-btn" onClick={() => setResetTarget(u)} data-testid={`pc-install-reset-${u.user_id}`}>
                      <KeyRound className="h-3.5 w-3.5 inline mr-1" />Reset password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pc-card">
          <div className="pc-card-h"><h2>Payment history</h2></div>
          <table className="pc-table">
            <thead><tr><th>When</th><th>Purpose</th><th className="pc-num">Amount</th><th>Status</th></tr></thead>
            <tbody>
              {invoices.map(i => (
                <tr key={i.invoice_id || i.created_at} data-testid={`pc-install-invoice-${i.invoice_id || i.created_at}`}>
                  <td>{when(i.created_at)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{(i.purpose || 'plan').replace('_', ' ')}</td>
                  <td className="pc-num pc-mono">{money((i.amount_pesewas || 0) / 100)}</td>
                  <td><Pill value={i.status} /></td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={4} className="pc-empty" data-testid="pc-install-no-invoices">No payments recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="pc-card" style={{ gridColumn: '1 / -1' }}>
          <div className="pc-card-h"><h2>Security timeline</h2></div>
          <table className="pc-table">
            <tbody>
              {securityEvents.map(e => (
                <tr key={e.identifier} data-testid={`pc-install-security-${e.identifier}`}>
                  <td style={{ width: 90 }}>{e.currently_locked ? <Pill value="suspended" /> : <Pill value="past_due" />}</td>
                  <td>{e.note}</td>
                  <td className="pc-num">{when(e.updated_at)}</td>
                </tr>
              ))}
              {securityEvents.length === 0 && <tr><td colSpan={3} className="pc-empty" data-testid="pc-install-no-security">No failed sign-ins recorded.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmAction
        open={!!resetTarget} title={`Reset password for ${resetTarget?.email || ''}`}
        description="Generates a one-time password and forces a change on next sign-in. Signs that user out everywhere."
        busy={busy} onCancel={() => setResetTarget(null)} onConfirm={doReset} testId="pc-install-reset-confirm"
      />

      <ConfirmAction
        open={!!tempPassword} title="Temporary password" requireReason={false}
        description={tempPassword ? `Share this with ${tempPassword.email} — it must be changed on next sign-in.` : ''}
        onCancel={() => setTempPassword(null)} onConfirm={() => setTempPassword(null)} testId="pc-install-temp-password"
      >
        <div className="pc-mono" style={{ fontSize: '1rem', padding: '.6rem', background: '#0b1016', borderRadius: 8 }}
          data-testid="pc-install-temp-password-value">{tempPassword?.temporary_password}</div>
      </ConfirmAction>

      <ConfirmAction open={contactOpen} title={`Update reminder contact · ${tenant.name}`} requireReason={false} busy={busy}
        description="Update the email and Ghana phone used for DutiLyne update reminders."
        onCancel={() => { setContactOpen(false); setParams({}, { replace: true }); }} onConfirm={saveContact} testId="pc-install-contact-confirm" confirmLabel="Save contact">
        <div className="pc-row"><div><label className="pc-field">Email</label><input className="pc-input" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="pc-install-contact-email-input" /></div><div><label className="pc-field">Ghana phone</label><input className="pc-input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="pc-install-contact-phone-input" /></div></div>
      </ConfirmAction>
    </div>
  );
};
